import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName } from '../MenuItemTags';
import { isMenuItemActive, resolveItemImageUrl } from '../menuEditHelpers';
import { readHighlightMenuSection } from './highlightMenuItems';

/** “Today” kicker + compact list over a shared image wash. */
export function MenuHighlightTodaySplit({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const { sectionTitle, sectionSubtitle, dishes } = readHighlightMenuSection(
    block,
    locale,
  );
  const target = isActive ? activeEditTarget : null;
  const list = dishes.slice(0, 4);
  const washSource = list[0];
  const washUrl = washSource
    ? resolveItemImageUrl(block, washSource.item, washSource.columnIndex, washSource.itemIndex)
    : undefined;
  const washSrc = washUrl ? buildSiteImageProxyUrl(washUrl, 1200, 55) : undefined;
  const washSrcSet = washUrl
    ? buildSiteImageSrcSet(washUrl, [640, 960, 1200], 55)
    : undefined;

  return (
    <section
      className={['ws-hl-today', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-hl-today__panel">
        {washUrl && washSource ? (
          <div className="ws-hl-today__wash" aria-hidden>
            <EditableMedia
              fieldKey={`itemImage_${washSource.columnIndex}_${washSource.itemIndex}`}
              editable={editable}
              activeEditTarget={activeEditTarget}
              className="ws-hl-today__wash-img"
              src={washSrc}
              srcSet={washSrcSet}
              sizes={SITE_IMAGE_SIZES.menuHighlightToday}
              alt=""
              width={1200}
              height={800}
              loading="eager"
              decoding="async"
            />
          </div>
        ) : (
          <div className="ws-hl-today__wash ws-hl-today__wash--tone" aria-hidden />
        )}
        <div className="ws-hl-today__content">
          <aside className="ws-hl-today__kicker">
            <EditableContent
              as="h2"
              fieldKey="sectionTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-hl-today__title"
            >
              {sectionTitle}
            </EditableContent>
            {sectionSubtitle || editable ? (
              <EditableContent
                as="p"
                fieldKey="sectionSubtitle"
                editable={editable}
                activeEditTarget={target}
                className="ws-hl-today__subtitle"
              >
                {sectionSubtitle}
              </EditableContent>
            ) : null}
          </aside>
          <ul className="ws-hl-today__list">
            {list.map(({ item, columnIndex, itemIndex }) => (
              <li
                key={`${columnIndex}-${itemIndex}`}
                className={[
                  'ws-hl-today__row',
                  isMenuItemActive(
                    target?.kind === 'menu-item' ? target : null,
                    columnIndex,
                    itemIndex,
                  )
                    ? 'ws-edit-child--active'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                {...(editable
                  ? {
                      'data-edit-child': 'menu-item' as const,
                      'data-column-index': columnIndex,
                      'data-item-index': itemIndex,
                    }
                  : {})}
              >
                <div className="ws-hl-today__story">
                  <div className="ws-hl-today__head">
                    <MenuItemName
                      item={item}
                      locale={locale}
                      className="ws-hl-today__name"
                      showLegalMarks={false}
                      showOrderCode={false}
                    />
                    <MenuItemDietaryTags
                      item={item}
                      locale={locale}
                      iconsOnly
                      placement="inline"
                    />
                  </div>
                  {item.description ? (
                    <p className="ws-hl-today__desc">{item.description}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
